import { type QiraMessage, QiraMessageContentChunk, ThreadWithoutMessagesDto } from 'api3';
import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Api3Service } from '@app/services/api3.service';
import { AuthService } from '@app/services/auth.service';
import type { ChatTab, DisplayVehicle, GetThreadMessagesOptions, SendMessage } from '@app/services/types';
import { VehicleService } from '@app/services/vehicle.service';
import { VoiceService } from '@app/services/voice.service';
import { ErrorDialogComponent } from '@app/components/error-dialog/error-dialog.component';
import { ModalCallbackProps, ModalService } from '@app/components/modal/modal.service';
import { ChatInputService } from './chat-input.service';
import { FeatureNoticeService } from './feature-notice.service';
import { PrimeNgMessageService } from './primeNgMessage.service';
import { SchemaValidationService } from './schema-validation.service';
import { toError } from './error-normalization-utils';
import { TranslocoService } from '@jsverse/transloco';
import * as Sentry from '@sentry/angular';

const DEFAULT_LLM_ANSWERS_ON = true;

@Injectable({
  providedIn: 'root',
})
export class TabService {
  private readonly router = inject(Router);
  private _openVehicleSelectionDialog: (() => void) | undefined = undefined;

  _tabs = signal<ChatTab[]>([]);
  // readonly observable
  tabs = computed(this._tabs);
  activeTab = signal<ChatTab | undefined>(undefined);
  activeTabIndex = signal<number>(0);
  tabsLoading = signal<boolean>(true);
  llmAnswersOn = signal<boolean>(this.getInitialState());

  authService = inject(AuthService);
  api3Service = inject(Api3Service);
  schemaValidationService = inject(SchemaValidationService);
  vehiclesService = inject(VehicleService);
  featureNoticeService = inject(FeatureNoticeService);
  chatInputService = inject(ChatInputService);
  voiceService = inject(VoiceService);
  translocoService = inject(TranslocoService);
  messageService = inject(PrimeNgMessageService);
  modalService = inject(ModalService);

  POLLING_TIMEOUT = 5 * 60 * 1000; // 5 minutes

  initLlmUsageState = effect(() => {
    localStorage.setItem('llmEnabledState', JSON.stringify(this.llmAnswersOn()));
  });

  userUpdated = effect(() => {
    const user = this.authService.currentUser();
    if (user) {
      this.loadThreads().then(() => {
        // set active tab causes the messages load
        const activeTab = this.getTabById(this.activeTab()?._id);
        if (activeTab) {
          this._setActiveTab(activeTab);
        }
      });
    } else {
      this._tabs.set([]);
      this.tabsLoading.set(false);
    }
  });

  registerVehicleSelectionFunction(openVehicleSelectionRef: () => void) {
    this._openVehicleSelectionDialog = openVehicleSelectionRef;
  }

  openVehicleSelectionDialog() {
    if (this._openVehicleSelectionDialog) {
      this._openVehicleSelectionDialog();
    }
  }

  showFeatureNotice(openVehicleSearchAfterClose = false, showAgain = false): void {
    this.featureNoticeService.open(
      { forceShow: showAgain },
      {
        onOkButtonClick: () => {
          this._afterFeatureNoticeClose(true);
        },
        onBackdropClick: (modal: ModalCallbackProps) => {
          if (openVehicleSearchAfterClose) {
            this._afterFeatureNoticeClose(false);
          }
          modal.close();
        },
        onCloseButtonClick: (modal: ModalCallbackProps) => {
          if (openVehicleSearchAfterClose) {
            this._afterFeatureNoticeClose(false);
          }
          modal.close();
        },
        onEscape: (modal: ModalCallbackProps) => {
          if (openVehicleSearchAfterClose) {
            this._afterFeatureNoticeClose(false);
          }
          modal.close();
        },
      },
    );
  }

  openTab(tabId: string | undefined) {
    const tab = this.getTabById(tabId);
    if (!tab || tab._id !== this.activeTab()?._id) {
      this._setActiveTab(tab);
    }
  }

  async loadThreads() {
    const loggedIn = await this.authService.isLoggedIn();
    if (!loggedIn) return;

    this.tabsLoading.set(true);

    try {
      const threads = await this._requestThreads();
      this._tabs.set(threads);
      this.tabsLoading.set(false);

      // start polling, if there is an open question
      this._restartPolling();
    } catch (error) {
      this.authService.handleNotLoggedInError(error);

      this.tabsLoading.set(false);
      this._openErrorDialog();

      console.error('[TabService] loadThreads() error:', error);
      Sentry.captureException(toError(error), {
        tags: {
          location: 'TabService',
          feature: 'QiraThreads',
          message: 'Failed to load all tabs.',
        },
      });
    }
  }

  getTabById(tabId: string | undefined) {
    if (!tabId) return;
    return this.tabs().find((tab) => tab._id === tabId);
  }

  async createTab(chosenVehicle: DisplayVehicle): Promise<ChatTab | undefined> {
    if (!chosenVehicle || !chosenVehicle.vehicleIdentification) {
      throw new Error('Vehicle ID is required');
    }

    const { data: newTab, error } = await this.api3Service.api.threads.create({
      title: chosenVehicle.name,
      vehicleIdentification: {
        ...chosenVehicle.vehicleIdentification,
        // Since we switched to tecDoc for vehicle identification, engineCode is required
        engineCode: chosenVehicle.engineCode || undefined,
      },
    });

    if (error) {
      console.error('[TabService] Creating new thread failed with', error);
      Sentry.captureException(toError(error), {
        tags: {
          location: 'TabService',
          feature: 'QiraThreads',
          message: 'Creating new thread failed.',
        },
      });
      throw error;
    }

    const displayTab = await this._addVehicleData(newTab);
    this._tabs.update((tabs) => [displayTab, ...tabs]);
    this.openTab(displayTab._id);

    return displayTab;
  }

  async deleteTab(threadId: string) {
    const { error } = await this.api3Service.api.threads.remove(threadId);

    if (error) {
      console.error('[TabService] Deleting thread failed with', error);
      Sentry.captureException(toError(error), {
        tags: {
          location: 'TabService',
          feature: 'QiraThreads',
          message: 'Failed to delete a tab.',
        },
      });
      await this.messageService.showError('chat.error.delete.thread');
      return;
    }

    this._tabs.update((tabs) => tabs.filter((tab) => tab._id !== threadId));
  }

  async submitQuestion(sendMessage: SendMessage): Promise<QiraMessage | undefined> {
    if (!this.activeTab()) {
      throw new Error('No active tab');
    }

    const tabToUpdate = this.tabs().find((tab) => tab._id === sendMessage.threadId);
    if (!tabToUpdate) return undefined;

    // is tab already busy?
    if (tabToUpdate.isPolling) return undefined;

    this._startPolling(tabToUpdate);

    // add temp question immediately
    this._tabs.update((tabs) => {
      return tabs.map((tab) => {
        if (tab._id === tabToUpdate._id) {
          tab.messages = tab.messages.concat({
            id: 'tmp',
            created_at: new Date().toISOString(),
            sender: 'you',
            type: 'end', // "end" | "info" | "error" | "stream" | "feedback"
            content: [
              {
                text: sendMessage.text,
              } as QiraMessageContentChunk,
            ],
          });
          return tab;
        }
        return tab;
      });
    });
    this._informActiveTabObservers();

    const activeSystem: unknown = this.llmAnswersOn() ? 'qiraAI' : 'recon';
    const { data: message, error } = await this.api3Service.api.threads.createMessage(sendMessage.threadId, {
      text: sendMessage.text,
      qiraSystem: activeSystem as object,
    });

    if (error) {
      console.error('[TabService] Error sending message', error);

      const isThreadDeleted = error?.message?.includes('NotFoundException') || error?.status === 400 || error?.status === 404;

      if (isThreadDeleted) {
        // Mark the tab as deleted so the UI can react
        this._tabs.update((tabs) => tabs.map((tab) => (tab._id === sendMessage.threadId ? { ...tab, isDeleted: true } : tab)));
        this._informActiveTabObservers();
        this._stopPolling(tabToUpdate);
        return;
      }

      // Generic error — still worth tracking
      Sentry.captureException(toError(error), {
        tags: {
          location: 'TabService',
          feature: 'QiraThreads',
          message: 'Failed to send a message.',
        },
      });
      await this.messageService.showError('chat.error.send.message');
      return;
    }

    // add new message (question) to messages list
    this._tabs.update((tabs) => {
      const newList = tabs.map((tab) => {
        if (tab._id === tabToUpdate?._id) {
          this._addMessage(tab, message);
        }
        return tab;
      });
      return [...newList];
    });

    this._informActiveTabObservers();
    this._pollMessages(tabToUpdate);

    return message;
  }

  isChatQuestion(message: QiraMessage | undefined) {
    if (!message) return false;
    return message.sender === 'you';
  }

  isInfoMessage(message: QiraMessage | undefined) {
    if (!message) return false;
    return message?.sender === 'bot' && message?.type === 'info';
  }

  isChatAnswer(message: QiraMessage | undefined) {
    if (!message) return false;
    return message.sender === 'bot' && message.type !== 'info';
  }

  async updateMessage(threadId: string, message: Partial<QiraMessage>): Promise<QiraMessage | undefined> {
    const messageId = message.id;
    if (!messageId) {
      console.error('[TabService] Cannot update message without id');
      return;
    }
    const { data, error } = await this.api3Service.api.threads.updateMessage(threadId, messageId, {
      voting: message.voting,
      feedback: message.feedback,
    });

    if (error) {
      console.error('[TabService] Error updating message', error);
      Sentry.captureException(toError(error), {
        tags: {
          location: 'TabService',
          feature: 'QiraThreads',
          message: 'Failed to update a message.',
        },
      });
      throw error;
    }

    return data;
  }

  private async _setActiveTab(tab: ChatTab | undefined) {
    const currentTabIndex = this._tabs().findIndex((t) => t._id === tab?._id);
    this.activeTab.set(tab);
    this.activeTabIndex.set(currentTabIndex);

    if (!tab) return;

    const messages = await this._requestMessages(tab);

    this._tabs.update((tabs) => {
      return tabs.map((t) => {
        if (t._id === tab._id) {
          t.messages = messages;
        }
        return t;
      });
    });
    this._informActiveTabObservers();
  }

  private _informActiveTabObservers() {
    const tab = this._tabs().find((t) => t._id === this.activeTab()?._id);
    this.activeTab.update(() => (tab ? { ...tab } : undefined));
  }

  private async _requestThreads(): Promise<ChatTab[]> {
    // Get current URL and extract tabId
    // this solution is a little hacky and i chose it for simplicity
    // I am working under two main contraints:
    // 1) threads.findAll usually doesnt return threads from other users, and shouldnt even for an admin because those would be many
    // 2) the FE in ChatComponent._setActiveTabOrRedirect redirects to the first known tab if the current tab in the url is unknown
    // to work around these .findAll accepts an additonal tab to load (the BE checks for the permission)
    //   this way the tabs array just contains the tab we want to view
    // and by accessing and parsing the url diretcly i dont need to pass dependencies around as much
    //  and that would be awkard, because tab loads before ChatComponent, but ChatComponent has the param we need
    //    that would most likely cause another api request + ui redraw for no reason
    const currentUrl = this.router.url;
    const tabIdMatch = currentUrl.match(/\/chat\/([^/?]+)/);
    const tabId = tabIdMatch ? tabIdMatch[1] : '';

    const { data: threads, error } = await this.api3Service.api.threads.findAll({ additionalThreadId: tabId });

    if (error) {
      console.error('[TabService] Loading threadList failed with', error);
      Sentry.captureException(toError(error), {
        tags: {
          location: 'TabService',
          feature: 'QiraThreads',
          message: 'Loading threadList failed.',
        },
        extra: {
          message: (error as any).message,
          statusText: (error as any).statusText,
        },
      });
    }

    const valid = this.schemaValidationService.validate2('threads.findAll', threads);
    if (!valid) {
      console.warn('[TabService] Validation error:', valid);
    }

    if (!threads || threads.length === 0) {
      return [];
    }

    return await Promise.all(
      threads.map(async (thread: ThreadWithoutMessagesDto) => {
        return await this._loadVehicleData(thread);
      }),
    );
  }

  private async _addVehicleData(thread: ThreadWithoutMessagesDto): Promise<ChatTab> {
    if (thread.session.vehicle?.name) {
      return {
        ...thread,
        vehicle: {
          ...thread.session.vehicle,
          vehicleIdentification: thread.vehicleIdentification,
        },
        messages: [],
      } as ChatTab;
    }

    return await this._loadVehicleData(thread);
  }

  private async _loadVehicleData(thread: ThreadWithoutMessagesDto): Promise<ChatTab> {
    const emptyResponse = {
      ...thread,
      vehicle: undefined,
      messages: [],
    };

    const kTypeId = thread.session.vehicle?.id?.kTypeIdTecDoc;
    const carTypeId = thread.session.vehicle?.id?.carTypeId;

    if (!kTypeId && !carTypeId) {
      console.warn('[TabService] No vehicle id found for thread', thread._id);
      return emptyResponse;
    }

    let vehicle;
    let error;

    if (kTypeId) {
      const engineCode = thread.session.vehicle?.engineCode ?? '';
      const { data: vehicleByKTypeId, error: errorByKTypeId } = await this.api3Service.api.vehicles.getVehicleByKTypeId(kTypeId, engineCode);
      vehicle = vehicleByKTypeId;
      error = errorByKTypeId;

      if (errorByKTypeId) {
        console.error('[TabService] Loading vehicle with kTypeId', kTypeId, ' failed: ', errorByKTypeId);
      }
    } else if (carTypeId) {
      const { data: vehicleByCarTypeId, error: errorByCarTypeId } = await this.api3Service.api.vehicles.getVehicleByCarTypeId(carTypeId);
      vehicle = vehicleByCarTypeId;
      error = errorByCarTypeId;

      if (errorByCarTypeId) {
        console.error('[TabService] Loading vehicle with carTypeId', carTypeId, ' failed: ', errorByCarTypeId);
      }
    }

    let finalVehicle = vehicle;
    let finalError = error;

    if (error) {
      console.log('[TabService] Attempting fallback method...');

      const fallbackResult = await this.api3Service.api.vehicles.getVehicleFromDb(thread._id);
      finalVehicle = fallbackResult.data;
      finalError = fallbackResult.error;

      if (finalError) {
        Sentry.captureException(finalError, {
          tags: {
            location: 'TabService',
            feature: 'QiraThreads',
            message: `Loading vehicle failed. Given kTypeId: ${kTypeId}, carTypeId: ${carTypeId}`,
          },
        });
        throw finalError;
      }
    }

    if (!finalVehicle) {
      console.warn('[TabService] No vehicle found for thread', thread);
      return emptyResponse;
    }

    return {
      ...thread,
      vehicle: {
        ...finalVehicle,
      },
      vehicleIdentification: thread.vehicleIdentification,
      messages: [],
    };
  }

  private _addMessage(tab: ChatTab, message: QiraMessage) {
    const lastMessage = tab.messages.slice(-1)[0];
    if (lastMessage?.id === 'tmp') {
      tab.messages.pop();
    }
    tab.messages = tab.messages.concat(message);
    return tab;
  }

  private _restartPolling() {
    this.tabs().forEach((tab) => {
      const lastMessage = tab.messages.slice(-1)[0];

      if (this.isChatQuestion(lastMessage) || this.isInfoMessage(lastMessage)) {
        this._startPolling(tab);
        this._pollMessages(tab);
      }
    });
  }

  private async _pollMessages(tabToUpdate: ChatTab | undefined, delay = 1000) {
    if (!tabToUpdate) return;

    let index = 0;
    const interval = setInterval(async () => {
      try {
        const messages = await this._requestMessagesSince(tabToUpdate);

        // concat new messages to existing ones
        if (messages && messages.length > 0) {
          this._tabs.update((tabs) => {
            tabs.forEach((tab) => {
              if (tab._id === tabToUpdate._id) {
                tab.messages = tab.messages.concat(messages);
              }
            });

            return tabs;
          });

          this._informActiveTabObservers();
        }

        // stop polling if chat has answered
        const latestMessage = messages.slice(-1)[0];
        if (this.isChatAnswer(latestMessage)) {
          clearInterval(interval);
          this._onChatAnswer(latestMessage);
          this._stopPolling(tabToUpdate);
        }
      } catch (error) {
        this.authService.handleNotLoggedInError(error);

        console.error('[TabService] Error during polling', error);
        await this.messageService.showError('chat.error.load.messages');
      }

      // stop polling if timeout is reached
      index++;
      if (index * delay > this.POLLING_TIMEOUT) {
        console.log('clear interval because of timeout');
        clearInterval(interval);
        console.warn('[TabService] Polling timeout for tab', tabToUpdate._id);
        this._stopPolling(tabToUpdate);
        this._handlePollingTimeout(tabToUpdate);
      }
    }, delay);
  }

  private async _requestMessagesSince(tab: ChatTab): Promise<QiraMessage[]> {
    const lastMessage = tab.messages.slice(-1)[0];
    const lastMessageDate = lastMessage ? new Date(lastMessage.created_at) : new Date();

    return this._requestMessages(tab, {
      created_after: lastMessageDate.toISOString(),
    });
  }

  private async _requestMessages(tab: ChatTab, filters?: GetThreadMessagesOptions['filters']): Promise<QiraMessage[]> {
    const { data: messages, error } = await this.api3Service.api.threads.findAllMessages(tab._id, { ...filters });

    if (error) {
      console.error('[TabService] Error when loading messages for tab ', tab._id, ':', error);
      Sentry.captureException(toError(error), {
        tags: {
          location: 'TabService',
          feature: 'QiraThreads',
          message: 'Error when loading messages.',
        },
      });
      throw error;
    }

    return messages;
  }

  private _startPolling(tab: ChatTab) {
    tab.isPolling = true;
    tab.isPollingSince = new Date();
    if (tab._id === this.activeTab()?._id) {
      this._informActiveTabObservers();
    }
  }

  private _stopPolling(tab: ChatTab) {
    tab.isPolling = false;
    tab.isPollingSince = undefined;
    if (tab._id === this.activeTab()?._id) {
      this._informActiveTabObservers();
    }
  }

  private _onChatAnswer(message: QiraMessage) {
    this.voiceService.readOutLoud(message);
  }

  private _openErrorDialog() {
    this.modalService.open(
      ErrorDialogComponent,
      {},
      {
        screen: 'auto',
        showCloseButton: true,
      },
    );
  }

  private getInitialState(): boolean {
    const savedState = localStorage.getItem('llmEnabledState');
    return savedState ? JSON.parse(savedState) : DEFAULT_LLM_ANSWERS_ON;
  }

  private _handlePollingTimeout(tabToUpdate: ChatTab) {
    // Show no-answer-message
    this._tabs.update((tabs) => {
      return tabs.map((tab) => {
        if (tab._id === tabToUpdate._id) {
          tab.messages = tab.messages.concat({
            id: 'tmp',
            sender: 'bot',
            type: 'stream',
            content: [
              {
                text: this.translocoService.translate('chatMessages.error.timeout'),
              },
            ],
          } as QiraMessage);
          return tab;
        }
        return tab;
      });
    });

    this._informActiveTabObservers();
  }

  private _afterFeatureNoticeClose(openVehicleSearchAfterClose = false) {
    if (this.tabs().length === 0 || openVehicleSearchAfterClose) {
      this.openVehicleSelectionDialog();
    } else {
      this.chatInputService.clearChatInput(undefined);
      setTimeout(() => {
        this.chatInputService.setChatInput(this.activeTab()?._id, '');
      }, 10);
    }
  }
}
